import { reactive } from 'vue';

// Global toast state
const toastState = reactive({
  toasts: [],
  nextId: 1,
});

/**
 * Composable for showing toast notifications
 *
 * Usage:
 * const { showToast, showSuccess, showError, showWarning, showInfo } = useToast();
 *
 * showSuccess('Änderungen gespeichert!');
 * showError('Fehler beim Speichern.');
 * showToast('Custom message', 'info', 5000);
 */
export function useToast() {
  /**
   * Show a toast notification
   * @param {string} message - The message to display
   * @param {string} type - Type of toast: 'success', 'error', 'warning', 'info'
   * @param {number} duration - Duration in ms (0 = no auto-dismiss)
   * @returns {number} - Toast ID for manual dismissal
   */
  const showToast = (message, type = 'info', duration = 3000) => {
    const id = toastState.nextId++;
    const toast = {
      id,
      message,
      type,
      duration,
      dismissible: true,
    };

    toastState.toasts.push(toast);

    // Auto-remove after animation completes
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration + 500); // Duration + fade-out animation
    }

    return id;
  };

  /**
   * Remove a specific toast by ID
   * @param {number} id - Toast ID to remove
   */
  const removeToast = (id) => {
    const index = toastState.toasts.findIndex((t) => t.id === id);
    if (index !== -1) {
      toastState.toasts.splice(index, 1);
    }
  };

  /**
   * Clear all toasts
   */
  const clearAll = () => {
    toastState.toasts = [];
  };

  // Convenience methods
  const showSuccess = (message, duration = 3000) => showToast(message, 'success', duration);
  const showError = (message, duration = 5000) => showToast(message, 'error', duration);
  const showWarning = (message, duration = 4000) => showToast(message, 'warning', duration);
  const showInfo = (message, duration = 3000) => showToast(message, 'info', duration);

  return {
    toasts: toastState.toasts,
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    removeToast,
    clearAll,
  };
}
